import Vue from 'vue';
import VueRouter from 'vue-router';
import { ApplicationBase } from '../core/ApplicationBase';
import qs from 'qs';

// Application singleton
let _instance = null;

export class VueApplication extends ApplicationBase {
    /**
     * Create new VueApplication instance.
     *
     * @param {string} mountPoint - Selector for the node to mount the App
     */
    constructor(mountPoint = '#app-mount-point') {
        super();

        if (_instance) {
            return _instance;
        }
        _instance = this;

        this.mountPoint = mountPoint;
        // The Vue router instance
        this.router = null;

        this.vm = null;
        this.mainApp = null;
    }

    /**
     * Start the application. Create the Vue root and start the router.
     *
     * @param {Object} config - Vue custom config
     */
    startApp(config = {}) {
        // Create router based application
        if (this.router) {
            this.registerPlugins([VueRouter]);
            config.router = this.router;
        }
        // Mount the app
        config.render = h => h(this.mainApp);

        this.vm = new Vue(config).$mount(this.mountPoint);
    }

    /**
     * Configure the Router for the application
     *
     * @param {object} routes - VueRouter router list
     * @param {object} config - VueRouter config
     */
    configureRoutes(routes, config = null) {
        if (typeof VueRouter !== 'function') {
            throw Error('VueRouter is not installed');
        }

        // Use config or default
        const c = config || {
            mode: 'history',
            routes,
            scrollBehavior(to, from, savedPosition) {
                if (savedPosition) {
                    return savedPosition;
                }

                if (to.hash) {
                    return {
                        selector: to.hash,
                    };
                }

                return { x: 0, y: 0 };
            },
            parseQuery(query) {
                return qs.parse(query);
            },
            stringifyQuery(query) {
                const result = qs.stringify(query, { encodeValuesOnly: true });

                if (result) {
                    return `?${result}`;
                }

                return '';
            },
        };

        this.router = new VueRouter(c);

        /**
         * This is an error handling *hook*, which does not replace VueRouter's own error
         * handling. This means we don't need to rethrow the error in order to pass it
         * back to VueRouter, which runs its default error handling after the hooks.
         *
         * @link https://github.com/vuejs/vue-router/blob/306602d8a4519d83195d02482bc6e0f40bb9d747/src/history/base.js#L95
         */
        this.router.onError((error) => {
            if (error.name === 'ChunkLoadError') {
                window.location.reload();
            }
        });
    }

    /**
     * Register the Vue plugins to use
     *
     * @param {array} list - List of objects to register as VuePlugins
     */
    registerPlugins(list) {
        list.map(el => Vue.use(el));

        // Install this too
        Vue.use(this);
    }

    /**
     * Register a library of common components globally for
     * use throughout the entire app.
     *
     * @param {object} components - An object that defines a set of global components
     */
    registerComponents(components) {
        this.mainApp = components.MainApp;

        Object.keys(components).map((key) => {
            Vue.component(key, components[key]);
        });
    }


    /**
     * Registers given set of directives to be globally available within the app
     *
     * @param directives
     */
    registerDirectives(directives) {
        directives.forEach((directive) => {
            Vue.directive(directive.name, directive.options);
        });
    }

    /**
     * Vue Plugin install method that exposes provider data and creates
     * a list of computed properties.
     */
    install() {
        // Add the current app to the Vue prototype for global use
        Vue.prototype.$app = this;
    }

    /**
     * Get the current route
     *
     * If no router is present or the app is unmounted null is returned
     *
     * @readonly
     *
     * @memberOf VueApplication
     */
    get route() {
        return this.vm.$route || null;
    }
}
